import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import core, { App } from '@gin-betting/core';
import { OrderedSet } from 'immutable';
import { createSelector } from 'reselect';

import { useMemoSelector, useSportType } from 'hooks';
import { getFeaturedZoneConfigs } from '../actions';
import { FEATURED_ZONES_MAPPING } from '../constants';
import {
    selectFeaturedTournamentsFetchStatus,
    selectFeaturedTournamentsList,
} from '../selectors';
import type { FeaturedConfig } from '../types';

interface Output {
    featuredConfigs: OrderedSet<FeaturedConfig>;
    fetchStatus: App.FetchStatus;
}

const {
    poller: { getPollerManager },
    entities: {
        selectors: { selectSports },
    },
} = core;

const selectProps = createSelector(
    selectFeaturedTournamentsList,
    selectFeaturedTournamentsFetchStatus,
    selectSports,
    (featuredConfigs, fetchStatus, sports) => ({
        featuredConfigs,
        fetchStatus,
        sports,
    })
);

const useFeaturedConfigs = (): Output => {
    useLocation();
    const dispatch = useDispatch();

    const { featuredConfigs, fetchStatus, sports } = useMemoSelector(
        selectProps
    );

    const sportType = useSportType();

    // NOTE: Sports are passed to callback via ref to avoid obsolete effect calls.
    // We rely on fact that sports are fetched only once,
    // so the "sports.size" change from 0 to any value is the only valid change
    const sportsRef = useRef(sports);
    sportsRef.current = sports;

    const zoneId = FEATURED_ZONES_MAPPING[sportType];

    const fetchFeaturedZoneConfigs = useCallback(
        (isPolling: boolean = false) => {
            dispatch(
                getFeaturedZoneConfigs(
                    { zoneId },
                    { isPolling, allSports: sportsRef.current }
                )
            );
        },
        [dispatch, zoneId]
    );

    useEffect(() => {
        if (sports.size === 0) return;

        fetchFeaturedZoneConfigs();

        const pollInterval$ = getPollerManager().getIntervalObserve();
        const pollerIntervalSubscription = pollInterval$.subscribe(() => {
            fetchFeaturedZoneConfigs(true);
        });

        return () => {
            pollerIntervalSubscription.unsubscribe();
        };
    }, [fetchFeaturedZoneConfigs, sports.size]);

    return {
        featuredConfigs,
        fetchStatus,
    };
};

export default useFeaturedConfigs;
